import { AccountClient } from '@frontend/account/api';
import { APIClient, ApiQueryParams } from '@frontend/api-utils';
import { AttributeClient, AttributeValueClient } from '@frontend/attribute/api';
import { AuditLogListResponse } from '@frontend/audit/types';
import { BasketClient, BasketItemClient } from '@frontend/basket/api';
import { BudgetClient, BudgetEntityClient, BudgetRuleClient, BudgetUseClient } from '@frontend/budget/api';
import { CertificateClient, CertificateRequirementClient, EquipmentCertificateClient, UserCertificateClient } from '@frontend/certificate/api';
import { ColliClient, ColliContentClient } from '@frontend/colli/api';
import { EntityType } from '@frontend/common';
import { ConstraintClient } from '@frontend/constraint/api';
import { CostCenterClient, CostCenterEntityClient } from '@frontend/cost-center/api';
import { DeviceClient } from '@frontend/device/api';
import { DocumentClient } from '@frontend/document/api';
import { EquipmentVariableClient } from '@frontend/equipment-variable/api';
import { EquipmentClient, EquipmentUseClient } from '@frontend/equipment/api';
import { EventClient } from '@frontend/events/api';
import { FunctionalityClient } from '@frontend/functionality/api';
import { EquipmentGroupClient, ProductCategoryClient, ProductGroupClient, QuestionnaireClient, UserGroupClient } from '@frontend/group/api';
import { IdentificationClient, IdentificationTagClient } from '@frontend/identification/api';
import { IoTClient } from '@frontend/iot/api';
import { LabelClient, LabelTemplateClient, LabelTemplateContentClient } from '@frontend/label/api';
import { LockerClient } from '@frontend/locker/api';
import { ModuleClient, ModuleTemplateClient } from '@frontend/module/api';
import { NotificationContentClient, NotificationTemplateClient } from '@frontend/notification/api';
import { PackageClient } from '@frontend/package/api';
import { PlaylistClient, PlaylistItemClient } from '@frontend/playlist/api';
import { ProblemClient, ProblemEntityClient } from '@frontend/problem/api';
import { ProductAttributeClient, ProductClient } from '@frontend/product/api';
import { AnswerClient, QuestionClient } from '@frontend/question/api';
import { ReportClient, JobClient as ReportJobClient, ScheduleClient as ReportScheduleClient } from '@frontend/report/api';
import { RoleClient } from '@frontend/role/api';
import { SettingClient } from '@frontend/setting/api';
import { SlotClient, SlotTemplateClient } from '@frontend/slot/api';
import { SpotClient } from '@frontend/spot/api';
import { SSOProviderClient } from '@frontend/sso/api';
import { RequirementClient, StockClient } from '@frontend/stock/api';
import { TransactionClient } from '@frontend/transaction/api';
import { UserInterfaceButtonClient } from '@frontend/user-interface-button/api';
import { UserInterfaceClient } from '@frontend/user-interface/api';
import { InviteClient, UserClient } from '@frontend/user/api';
import { DockerImageClient } from '@frontend/version/api';
import { StepClient, WizardClient } from '@frontend/wizard/api';
import { WorkflowStepClient } from '@frontend/workflow-step/api';
import { WorkflowClient, WorkflowFieldClient } from '@frontend/workflow/api';

export class AuditClient extends APIClient {
    public static async fetchAuditLogs(entityType: EntityType, entityId: string): Promise<AuditLogListResponse> {
        const endpoint = this.getEndpointForEntity(entityType);

        return await this.apiPaginated<AuditLogListResponse, string>(`${endpoint}/logs`, {
            entity_type: entityType,
            entity_id: entityId
        } as ApiQueryParams<string>);
    }

    private static getEndpointForEntity(entityType: EntityType): string {
        const endpoints: Partial<Record<EntityType, string>> = {
            [EntityType.ACCOUNT]: AccountClient.endpoint,
            [EntityType.ANSWER]: AnswerClient.endpoint,
            [EntityType.ATTRIBUTE]: AttributeClient.endpoint,
            [EntityType.ATTRIBUTE_VALUE]: AttributeValueClient.endpoint,
            [EntityType.BASKET]: BasketClient.endpoint,
            [EntityType.BASKET_ITEM]: BasketItemClient.endpoint,
            [EntityType.BUDGET]: BudgetClient.endpoint,
            [EntityType.BUDGET_ENTITY]: BudgetEntityClient.endpoint,
            [EntityType.BUDGET_RULE]: BudgetRuleClient.endpoint,
            [EntityType.BUDGET_USE]: BudgetUseClient.endpoint,
            [EntityType.CERTIFICATE]: CertificateClient.endpoint,
            [EntityType.CERTIFICATE_REQUIREMENT]: CertificateRequirementClient.endpoint,
            [EntityType.COLLI]: ColliClient.endpoint,
            [EntityType.COLLI_CONTENT]: ColliContentClient.endpoint,
            [EntityType.CONSTRAINT]: ConstraintClient.endpoint,
            [EntityType.COST_CENTER]: CostCenterClient.endpoint,
            [EntityType.COST_CENTER_ENTITY]: CostCenterEntityClient.endpoint,
            [EntityType.DEVICE]: DeviceClient.endpoint,
            [EntityType.DOCKER_IMAGE]: DockerImageClient.endpoint,
            [EntityType.DOCUMENT]: DocumentClient.endpoint,
            [EntityType.EQUIPMENT]: EquipmentClient.endpoint,
            [EntityType.EQUIPMENT_CERTIFICATE]: EquipmentCertificateClient.endpoint,
            [EntityType.EQUIPMENT_GROUP]: EquipmentGroupClient.endpoint,
            [EntityType.EQUIPMENT_USE]: EquipmentUseClient.endpoint,
            [EntityType.EQUIPMENT_VARIABLE]: EquipmentVariableClient.endpoint,
            [EntityType.EVENT]: EventClient.endpoint,
            [EntityType.FUNCTIONALITY]: FunctionalityClient.endpoint,
            [EntityType.GROUP]: UserGroupClient.endpoint,
            [EntityType.IDENTIFICATION]: IdentificationClient.endpoint,
            [EntityType.INVITE]: InviteClient.endpoint,
            [EntityType.IOT]: IoTClient.endpoint,
            [EntityType.LABEL]: LabelClient.endpoint,
            [EntityType.LABEL_TEMPLATE]: LabelTemplateClient.endpoint,
            [EntityType.LABEL_TEMPLATE_CONTENT]: LabelTemplateContentClient.endpoint,
            [EntityType.LOCKER]: LockerClient.endpoint,
            [EntityType.MODULE]: ModuleClient.endpoint,
            [EntityType.MODULE_TEMPLATE]: ModuleTemplateClient.endpoint,
            [EntityType.NOTIFICATION_CONTENT]: NotificationContentClient.endpoint,
            [EntityType.NOTIFICATION_TEMPLATE]: NotificationTemplateClient.endpoint,
            [EntityType.PACKAGE]: PackageClient.endpoint,
            [EntityType.PLAYLIST]: PlaylistClient.endpoint,
            [EntityType.PLAYLIST_ITEM]: PlaylistItemClient.endpoint,
            [EntityType.PROBLEM]: ProblemClient.endpoint,
            [EntityType.PROBLEM_ENTITY]: ProblemEntityClient.endpoint,
            [EntityType.PRODUCT]: ProductClient.endpoint,
            [EntityType.PRODUCT_ATTRIBUTE]: ProductAttributeClient.endpoint,
            [EntityType.PRODUCT_CATEGORY]: ProductCategoryClient.endpoint,
            [EntityType.PRODUCT_GROUP]: ProductGroupClient.endpoint,
            [EntityType.QUESTION]: QuestionClient.endpoint,
            [EntityType.QUESTIONNAIRE]: QuestionnaireClient.endpoint,
            [EntityType.REPORT]: ReportClient.endpoint,
            [EntityType.REPORT_JOB]: ReportJobClient.endpoint,
            [EntityType.REPORT_SCHEDULE]: ReportScheduleClient.endpoint,
            [EntityType.ROLE]: RoleClient.endpoint,
            [EntityType.SETTING]: SettingClient.endpoint,
            [EntityType.SLOT]: SlotClient.endpoint,
            [EntityType.SLOT_TEMPLATE]: SlotTemplateClient.endpoint,
            [EntityType.SPOT]: SpotClient.endpoint,
            [EntityType.SSO_PROVIDER]: SSOProviderClient.endpoint,
            [EntityType.STOCK]: StockClient.endpoint,
            [EntityType.STOCK_REQUIREMENT]: RequirementClient.endpoint,
            [EntityType.TAG]: IdentificationTagClient.endpoint,
            [EntityType.TRANSACTION]: TransactionClient.endpoint,
            [EntityType.USER]: UserClient.endpoint,
            [EntityType.USER_CERTIFICATE]: UserCertificateClient.endpoint,
            [EntityType.USER_GROUP]: UserGroupClient.endpoint,
            [EntityType.USER_INTERFACE]: UserInterfaceClient.endpoint,
            [EntityType.USER_INTERFACE_BUTTON]: UserInterfaceButtonClient.endpoint,
            [EntityType.WIZARD]: WizardClient.endpoint,
            [EntityType.WIZARD_STEP]: StepClient.endpoint,
            [EntityType.WORKFLOW]: WorkflowClient.endpoint,
            [EntityType.WORKFLOW_FIELD]: WorkflowFieldClient.endpoint,
            [EntityType.WORKFLOW_STEP]: WorkflowStepClient.endpoint
        };
        return endpoints[entityType] ?? 'undefined-endpoint';
    }
}
